import "src/global.css"

import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
// ----------------------------------------------------------------------
import * as Sentry from "@sentry/browser"
import timezone from "dayjs/plugin/timezone"
import { useEffect } from "react"
import { useLocation } from "react-router-dom"

import { Router } from "src/routes/sections"

import { useScrollToTop } from "src/hooks/use-scroll-to-top"

import { LocalizationProvider } from "src/locales"
import { ThemeProvider } from "src/theme/theme-provider"

import { ProgressBar } from "src/common/progress-bar"
import { MotionLazy } from "src/common/animate/motion-lazy"
import { SettingsDrawer } from "src/common/settings"
import { SplashScreen } from "src/common/loading-screen"
import { useMagicSourceInitialization } from "./common/magicSourceStatusProvider/magicSourceStatusProvider"

import { CONFIG } from "./config-global"
import { setTag } from "./sentry/sentry"
import { Snackbar } from "./common/snackbar"

// Import Zustand stores
import { useAuthStore, useAppConfigStore } from "./store"
import { useWorkspaceToggles } from "./services/workspaceToggles"

dayjs.extend(utc)
dayjs.extend(timezone)

// ----------------------------------------------------------------------
if (import.meta.env.MODE !== "development")
    Sentry.init({
        dsn: CONFIG.site.sentryUrl,
        environment: CONFIG.site.env,
        release: `ATS-PPH`,
        integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
        tracesSampleRate: 1.0,
        tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
    })

if (import.meta.env.MODE !== "development")
    Sentry.configureScope((scope: any) => {
        setTag(scope)
    })

// Auth initialization component - runs before app loads
function AuthInitializer() {
    const checkUserSession = useAuthStore((state: any) => state.checkUserSession)

    useEffect(() => {
        // Initialize auth session with error handling
        checkUserSession(true).catch((err: any) => {
            console.error("Failed to initialize auth:", err)
        })
    }, [checkUserSession])

    return null
}

// Store initialization component - runs after auth is ready
function StoreInitializer() {
    const location = useLocation()
    const fetchConfig = useAppConfigStore((state: any) => state.fetchConfig)
    const user = useAuthStore((state: any) => state.user)
    const isInternalUserPath = location.pathname.startsWith("/internal-user")

    // Initialize magic source functionality
    useMagicSourceInitialization()

    // Fetch workspace toggles when user can view agent features or billing monetization
    useWorkspaceToggles(!!user?.id && !isInternalUserPath)

    useEffect(() => {
        // Fetch config after user is authenticated
        if (user?.id) {
            fetchConfig().catch((err: any) => {
                console.error("Failed to fetch config:", err)
            })
        }
    }, [user?.id, fetchConfig])

    return null
}

export default function App() {
    const loading = useAuthStore((state: any) => state.loading)
    const { pathname } = useLocation()
    const isLanding = pathname === "/"
    useScrollToTop()

    return (
        <LocalizationProvider>
            <ThemeProvider>
                <AuthInitializer />
                {loading ? (
                    <SplashScreen />
                ) : (
                    <>
                        <StoreInitializer />
                        <MotionLazy>
                            {!isLanding && <ProgressBar />}
                            {!isLanding && (
                                <SettingsDrawer
                                    hidePresets
                                    hideNavColor
                                    hideContrast
                                    hideCompact
                                    hideDirection
                                    hideColorScheme
                                />
                            )}
                            <Snackbar />
                            <Router />
                        </MotionLazy>
                    </>
                )}
            </ThemeProvider>
        </LocalizationProvider>
    )
}
